import * as React from "react";
import { useState, useEffect, useRef } from "react";
import {
  Stack,
  Text,
  TextField,
  Spinner,
  SpinnerSize,
  Icon,
  mergeStyles,
  getTheme,
} from "@fluentui/react";
import { apiBaseUrl } from "../../../auth/authConfig";
import { authHeaders } from "../../../auth/authDialog";
import { BankInfo, SearchState } from "../types";

const API_URL = apiBaseUrl();
const theme = getTheme();

const RESULT_LIMIT = 20;
const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 300;

// Shared with CompanyScreener, which renders its results the same way.
export const resultListStyle = mergeStyles({
  border: `1px solid ${theme.palette.neutralLight}`,
  borderRadius: "4px",
  overflow: "hidden",
});

const resultRowStyle = mergeStyles({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  width: "100%",
  padding: "8px 12px",
  border: "none",
  borderBottom: `1px solid ${theme.palette.neutralLighter}`,
  backgroundColor: theme.palette.white,
  textAlign: "left",
  cursor: "pointer",
  selectors: {
    ":last-child": { borderBottom: "none" },
    ":hover": { backgroundColor: theme.palette.neutralLighterAlt },
    ":focus-visible": { outline: `2px solid ${theme.palette.themePrimary}`, outlineOffset: -2 },
  },
});

const selectedRowStyle = mergeStyles({
  backgroundColor: theme.palette.themeLighterAlt,
  selectors: {
    ":hover": { backgroundColor: theme.palette.themeLighter },
  },
});

export const ResultRow: React.FunctionComponent<{
  bank: BankInfo;
  selected: boolean;
  onSelect: (bank: BankInfo) => void;
}> = ({ bank, selected, onSelect }) => {
  const location = [bank.city, bank.state].filter(Boolean).join(", ");
  return (
    <button
      type="button"
      className={`${resultRowStyle} ${selected ? selectedRowStyle : ""}`}
      onClick={() => onSelect(bank)}
    >
      <Stack grow tokens={{ childrenGap: 2 }} styles={{ root: { minWidth: 0 } }}>
        <Text styles={{ root: { fontWeight: 600 } }}>{bank.name}</Text>
        <Text variant="small" styles={{ root: { color: theme.palette.neutralSecondary } }}>
          {location && `${location} · `}Cert #{bank.cert}
        </Text>
      </Stack>
      <Icon
        iconName="ChevronRight"
        styles={{ root: { fontSize: 12, color: theme.palette.neutralTertiary } }}
      />
    </button>
  );
};

export interface CompanySearchProps {
  search: SearchState;
  onSearchChange: (state: SearchState) => void;
  selectedCert: number | null;
  onSelect: (bank: BankInfo) => void;
}

const CompanySearch: React.FunctionComponent<CompanySearchProps> = ({
  search,
  onSearchChange,
  selectedCert,
  onSelect,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Monotonic id per fetch: a stale response (user kept typing) is dropped.
  const requestIdRef = useRef(0);

  const runSearch = async (query: string) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${API_URL}/banks/search?q=${encodeURIComponent(query)}&limit=${RESULT_LIMIT}`,
        { headers: authHeaders() }
      );
      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`);
      }
      const results: BankInfo[] = await response.json();
      if (requestId !== requestIdRef.current) return;
      onSearchChange({ ...search, query, searchedQuery: query, results });
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      setError(`Search failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  };

  // Debounced search-as-you-type. Skips when the current results already
  // belong to this query (e.g. remounting after navigating back from Info).
  useEffect(() => {
    const query = search.query.trim();
    if (query === search.searchedQuery) return;
    if (query.length < MIN_QUERY_LENGTH) {
      requestIdRef.current++; // cancel any in-flight response
      setLoading(false);
      setError(null);
      if (search.searchedQuery !== null) {
        onSearchChange({ ...search, searchedQuery: null, results: [] });
      }
      return;
    }
    const timer = setTimeout(() => runSearch(query), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search.query]);

  const handleEnter = () => {
    const query = search.query.trim();
    if (query.length >= MIN_QUERY_LENGTH && query !== search.searchedQuery) {
      runSearch(query);
    }
  };

  const searched = search.searchedQuery !== null;

  return (
    <Stack tokens={{ childrenGap: 12 }}>
      <TextField
        placeholder="Search by name, city, or cert #..."
        iconProps={{ iconName: "Search" }}
        value={search.query}
        onChange={(_e, val) => onSearchChange({ ...search, query: val || "" })}
        onKeyDown={(e) => e.key === "Enter" && handleEnter()}
      />

      {loading && <Spinner size={SpinnerSize.medium} label="Searching..." />}

      {error && <Text styles={{ root: { color: theme.palette.redDark } }}>{error}</Text>}

      {!loading && !error && searched && search.results.length === 0 && (
        <Text styles={{ root: { color: theme.palette.neutralSecondary } }}>
          No banks found matching “{search.searchedQuery}”.
        </Text>
      )}

      {!loading && !error && search.results.length > 0 && (
        <Stack tokens={{ childrenGap: 6 }}>
          <Text variant="small" styles={{ root: { color: theme.palette.neutralSecondary } }}>
            {search.results.length === RESULT_LIMIT
              ? `Top ${RESULT_LIMIT} matches — refine your search to narrow down`
              : `${search.results.length} match${search.results.length === 1 ? "" : "es"}`}
          </Text>
          <div className={resultListStyle}>
            {search.results.map((bank) => (
              <ResultRow
                key={bank.cert}
                bank={bank}
                selected={bank.cert === selectedCert}
                onSelect={onSelect}
              />
            ))}
          </div>
        </Stack>
      )}
    </Stack>
  );
};

export default CompanySearch;
